/**
 * Quote hygiene for Market text.
 *
 * The engine clips evidence quotes on a character budget, and the evidence itself is often lifted
 * from board packs and PDFs. On 2026-10-09 the unit4 SAM rendered quotes that:
 *   - started mid-sentence or on an agenda marker: `"D. Audit Reports 1. Board Office…"`
 *   - carried private-use glyphs from PDF bullet fonts: `"\uf076 What is Working Well \uf076 …"`
 *   - stopped mid-word with no closing quote mark: `"…1.4TB compressed hist`
 *
 * This file only fixes how a quote is DISPLAYED — markers, glyphs, whitespace, ellipses and the
 * closing mark. It cannot make a wrong quote right; that is a data fix on the stack cell.
 */

// Private-use area (PDF symbol fonts), replacement char, zero-width and control characters.
// Control characters are the point of this pattern, so the rule that flags them does not apply.
// eslint-disable-next-line no-control-regex
const JUNK_CHARS = /[\uE000-\uF8FF\uFFFD\u200B-\u200D\uFEFF\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;

// Leading agenda / list markers: "D. ", "1. ", "3.2.18 ", "iv) ", "• ", "- ", "– ".
const LEADING_MARKER = /^(?:(?:[A-Z]|[ivx]{1,4}|\d{1,3}(?:\.\d{1,3}){0,3})[.)]\s+|\d{1,3}(?:\.\d{1,3}){1,3}\s+|[•·▪◦‣∙\-–—*]\s+)+/;

const ELLIPSIS = '…';

/** Clean one quote's inner text (no surrounding quote marks). Returns '' when nothing is left. */
export function cleanQuote(raw, opts = {}) {
  let q = String(raw ?? '')
    .replace(JUNK_CHARS, ' ')
    .replace(/\.{3}/g, ELLIPSIS)
    .replace(/\s+/g, ' ')
    .trim();
  // Surrounding quote marks the caller will add back.
  q = q.replace(/^["“”'‘’]+|["“”'‘’]+$/g, '').trim();
  let cutHead = false;
  if (q.startsWith(ELLIPSIS)) { cutHead = true; q = q.slice(1).trim(); }
  const before = q;
  q = q.replace(LEADING_MARKER, '').trim();
  if (q !== before) cutHead = false; // a list marker is a clean start, not a cut
  let cutTail = false;
  if (q.endsWith(ELLIPSIS)) { cutTail = true; q = q.slice(0, -1).trim(); }
  // Dangling punctuation left by a character-budget clip: "…upgrading or replacing," → "…replacing".
  q = q.replace(/[\s,;:(\-–—]+$/, '').trim();
  if (!q) return '';
  // A start in lower case is a start mid-sentence. The tail is NOT inferred the same way: agenda
  // lines and headings ("Sole Source: Workday Consulting for Human Resources") are complete
  // without a full stop, so only a cut the engine marked, or an unclosed span, gets an ellipsis.
  if (/^[a-z]/.test(q)) cutHead = true;
  if (opts.unclosed && !/[.!?)"”’]$/.test(q)) cutTail = true;
  return `${cutHead ? ELLIPSIS : ''}${q}${cutTail ? ELLIPSIS : ''}`;
}

/**
 * Clean every quoted span inside a sentence such as a why-now or a driver detail:
 *   They have complained about Oracle on the record: "meetings, avatars. - Data Vault: … hist
 * becomes
 *   They have complained about Oracle on the record: “…meetings, avatars. - Data Vault: … hist…”
 * An unclosed span runs to the end of the string and is closed.
 */
export function cleanQuotedText(text) {
  const s = String(text ?? '');
  if (!s) return s;
  let out = '';
  let i = 0;
  while (i < s.length) {
    const open = s.slice(i).search(/["“]/);
    if (open < 0) { out += s.slice(i); break; }
    const start = i + open;
    out += s.slice(i, start);
    // An inch mark (12" screen) is not a quotation.
    if (s[start] === '"' && /\d/.test(s[start - 1] || '')) { out += '"'; i = start + 1; continue; }
    // Close on the SAME family of mark: a straight-quoted span often holds curly quotes of its
    // own ("move to “software as a service” (SAS)…"), and closing on the first ” cut it in two.
    const closer = s[start] === '"' ? /"/ : /”/;
    const closeRel = s.slice(start + 1).search(closer);
    const end = closeRel < 0 ? s.length : start + 1 + closeRel;
    const inner = cleanQuote(s.slice(start + 1, end), { unclosed: closeRel < 0 });
    if (inner) out += `“${inner}”`;
    else out = out.replace(/[\s:,]+$/, '');
    i = closeRel < 0 ? s.length : end + 1;
  }
  return out.replace(/\s+/g, ' ').trim();
}

/** Same filter as the engine's F4 detail: text that narrates our own research, not the institution. */
const PIPELINE = /second-pass|multilingual scan|governed corpus|attributable to this account|\bchunks?\b|\bcorpus\b|sitemap|https?:\/\/|document_id|measured absence|supplied URLs?|\bgrepp?ed\b|\bsweep\b|tag archive|confirmed absent|site search|at these addresses|passages of its own|\b(?:evidence|documents?|address(?:es)?|artefact|passages?)\b[^.]{0,40}\bheld\b|\bheld (?:here|for)\b/i;

export function narratesPipeline(text) {
  const t = String(text ?? '');
  return PIPELINE.test(t) || /\bGOVERNANCE\b/.test(t);
}
